import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, runInAction, set } from "mobx";
import moment from "moment";
import { routeLocationAffiliateCouponPost, routeLocationAffiliateCouponsGet, routeLocationAffiliateInviteGet, routeLocationAffiliateLinkPost, routeLocationAffiliatePatch, routeLocationAffiliatePayoutsGet, routeLocationAffiliateTransactionsGet } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../../exceptions/realCommerceError.js";
import { realCommerceRequest } from "../../utils/realCommerceRequest.js";
import { AffiliateCoupon } from "../affiliateCoupon.js";
class Affiliate {
    get affiliate() {
        return this.data.affiliate;
    }
    get commissions() {
        return this.data.commissions;
    }
    get invoiceAddress() {
        return this.affiliate.invoiceAddress;
    }
    get paymentDetails() {
        return this.affiliate.paymentDetails;
    }
    constructor(data, store){
        this.busyCoupons = false;
        this.busyTransactions = false;
        this.busyPayouts = false;
        this.coupons = new Map();
        this.statistics = [];
        this.transactions = new Map();
        this.payouts = new Map();
        this.transactionFilters = observable.object({
            dates: [
                undefined,
                undefined
            ]
        }, {}, {
            deep: false
        });
        this.createLink = flow(function*(target) {
            yield this.store.probablyRenewJwt();
            try {
                const { link } = yield realCommerceRequest({
                    location: routeLocationAffiliateLinkPost,
                    request: {
                        link: {
                            target
                        }
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                return link;
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            }
        });
        this.fetchTransactions = flow(function*() {
            this.busyTransactions = true;
            yield this.store.probablyRenewJwt();
            try {
                const { dates: [from, to] } = this.transactionFilters;
                const { statistics, transactions } = yield realCommerceRequest({
                    location: routeLocationAffiliateTransactionsGet,
                    params: {
                        from: from.toDate(),
                        to: to.toDate()
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.statistics = statistics;
                for (const row of transactions){
                    this.transactions.set(row.id, row);
                }
                // Remove stale transactions
                const activeIds = transactions.map(({ id })=>id);
                Array.from(this.transactions.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.transactions.delete(potentialStaleId);
                    }
                });
                // Additionally, update the global header
                this.store.fetchAffiliate();
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busyTransactions = false;
            }
        });
        this.fetchPayouts = flow(function*() {
            this.busyPayouts = true;
            yield this.store.probablyRenewJwt();
            try {
                const { payouts } = yield realCommerceRequest({
                    location: routeLocationAffiliatePayoutsGet,
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                for (const row of payouts){
                    this.payouts.set(row.id, row);
                }
                // Remove stale transactions
                const activeIds = payouts.map(({ id })=>id);
                Array.from(this.payouts.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.payouts.delete(potentialStaleId);
                    }
                });
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busyPayouts = false;
            }
        });
        this.fetchCoupons = flow(function*() {
            this.busyCoupons = true;
            yield this.store.probablyRenewJwt();
            try {
                const { coupons } = yield realCommerceRequest({
                    location: routeLocationAffiliateCouponsGet,
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                for (const row of coupons){
                    const { id } = row;
                    const existing = this.coupons.get(id);
                    if (existing) {
                        set(existing.data, row);
                    } else {
                        this.coupons.set(id, new AffiliateCoupon(row, this));
                    }
                }
                // Remove stale coupons
                const activeIds = coupons.map(({ id })=>id);
                Array.from(this.coupons.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.coupons.delete(potentialStaleId);
                    }
                });
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busyCoupons = false;
            }
        });
        this.createCoupon = flow(function*(data) {
            yield this.store.probablyRenewJwt();
            try {
                const { coupon } = yield realCommerceRequest({
                    location: routeLocationAffiliateCouponPost,
                    request: {
                        coupon: data
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.fetchCoupons();
                return coupon;
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            }
        });
        this.updateDetails = flow(function*(data) {
            yield this.store.probablyRenewJwt();
            try {
                const { affiliate } = yield realCommerceRequest({
                    location: routeLocationAffiliatePatch,
                    request: {
                        affiliate: data
                    },
                    multipart: true,
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.data.affiliate = affiliate;
                return affiliate;
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            }
        });
        this.store = store;
        this.applyTransactionDates([
            moment().subtract(30, "days"),
            moment()
        ]);
        runInAction(()=>{
            this.data = data;
        });
    }
    /**
     * This action does not actually refetch the transaction, you need to act on your components!
     *
     * @param dates
     */ applyTransactionDates(dates) {
        this.transactionFilters.dates = dates;
    }
    static{
        this.loadInvite = flow(function*(uuid) {
            try {
                const { invite } = yield realCommerceRequest({
                    location: routeLocationAffiliateInviteGet,
                    params: {
                        uuid
                    }
                });
                return invite;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponseAffiliateGet === "undefined" ? Object : IRouteResponseAffiliateGet)
], Affiliate.prototype, "data", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "busyCoupons", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "busyTransactions", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "busyPayouts", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "coupons", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Affiliate.prototype, "statistics", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "transactions", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "payouts", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "affiliate", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "commissions", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "invoiceAddress", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "paymentDetails", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], Affiliate.prototype, "applyTransactionDates", null);
export { Affiliate };
